<template>
  <div class="login-wrapper">
    <div class="login-container">
      <v-card class="login-card elevation-24">
        <!-- Header -->
        <div class="login-header">
          <div class="logo-container">
            <img
              src="/img/bice-logo.png"
              alt="Logo"
              class="logo-img"
            />
          </div>
          <h1 class="login-title">Nueva Contraseña</h1>
          <p class="login-subtitle">Ingresa tu nueva contraseña</p>
        </div>

            <!-- Success Message -->
            <div v-if="successMessage" class="success-container">
              <div class="success-icon-wrapper">
                <v-icon size="80" color="#4caf50">mdi-check-circle</v-icon>
              </div>

              <h2 class="success-title">¡Contraseña actualizada!</h2>
              <p class="success-message">{{ successMessage }}</p>

              <v-btn
                x-large
                block
                color="#28385C"
                dark
                @click="goToLogin"
                class="login-btn mt-6 mb-4"
                elevation="2"
              >
                <v-icon left>mdi-login</v-icon>
                Iniciar Sesión
              </v-btn>

              <p class="redirect-text">
                Serás redirigido automáticamente en {{ countdown }} segundo{{ countdown !== 1 ? 's' : '' }}...
              </p>
            </div>

            <!-- Form -->
            <v-card-text v-if="!successMessage" class="login-form-container">
              <v-form ref="form" v-model="valid" @submit.prevent="resetPassword">
                <p class="mb-4 text-body-2 grey--text text--darken-1">
                  Tu nueva contraseña debe tener al menos 6 caracteres.
                </p>

                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  label="Nueva Contraseña"
                  name="password"
                  prepend-inner-icon="mdi-lock-outline"
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                  @click:append="showPassword = !showPassword"
                  required
                  :disabled="loading"
                  outlined
                  dense
                  class="mb-3"
                  color="#28385C"
                  autofocus
                ></v-text-field>

                <v-text-field
                  v-model="passwordConfirmation"
                  :rules="passwordConfirmationRules"
                  label="Confirmar Contraseña"
                  name="password_confirmation"
                  prepend-inner-icon="mdi-lock-check-outline"
                  :type="showPasswordConfirmation ? 'text' : 'password'"
                  :append-icon="showPasswordConfirmation ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                  @click:append="showPasswordConfirmation = !showPasswordConfirmation"
                  required
                  :disabled="loading"
                  outlined
                  dense
                  class="mb-4"
                  color="#28385C"
                ></v-text-field>

                <v-alert
                  v-if="errorMessage"
                  type="error"
                  dismissible
                  dense
                  class="mb-4"
                  color="#d32f2f"
                >
                  {{ errorMessage }}
                </v-alert>

                <v-btn
                  block
                  x-large
                  color="#28385C"
                  dark
                  :loading="loading"
                  :disabled="!valid || loading"
                  @click="resetPassword"
                  class="login-btn mb-3"
                  elevation="2"
                >
                  <v-icon left>mdi-lock-reset</v-icon>
                  Actualizar contraseña
                </v-btn>

                <v-btn
                  block
                  text
                  color="#28385C"
                  @click="goToLogin"
                  :disabled="loading"
                  class="cancel-btn"
                >
                  Volver al inicio de sesión
                </v-btn>
              </v-form>
            </v-card-text>

        <!-- Footer -->
        <v-divider v-if="!successMessage"></v-divider>
        <div v-if="!successMessage" class="login-footer">
          <p class="footer-text">
            <v-icon small color="#666">mdi-shield-check</v-icon>
            Conexión segura y encriptada
          </p>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

export default {
  name: "ResetPassword",
  data() {
    return {
      valid: false,
      password: "",
      passwordConfirmation: "",
      showPassword: false,
      showPasswordConfirmation: false,
      loading: false,
      errorMessage: "",
      successMessage: "",
      resetPasswordToken: "",
      countdown: 5,
      countdownInterval: null,
      passwordRules: [
        v => !!v || "La contraseña es requerida",
        v => v.length >= 6 || "La contraseña debe tener al menos 6 caracteres"
      ],
      passwordConfirmationRules: [
        v => !!v || "Debes confirmar tu contraseña",
        v => v === this.password || "Las contraseñas no coinciden"
      ]
    };
  },
  mounted() {
    // Obtener el token de reset de la URL
    this.resetPasswordToken = this.$route.query.reset_password_token;

    if (!this.resetPasswordToken) {
      this.errorMessage = "Token inválido o expirado. Solicita un nuevo enlace.";
    }
  },
  beforeDestroy() {
    // Limpiar el intervalo cuando se destruye el componente
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  methods: {
    async resetPassword() {
      if (this.$refs.form.validate()) {
        if (!this.resetPasswordToken) {
          this.errorMessage = "Token inválido. Por favor solicita un nuevo enlace.";
          return;
        }

        this.loading = true;
        this.errorMessage = "";
        this.successMessage = "";

        try {
          const response = await Vue.axios.put(
            `${process.env.VUE_APP_API_URL}/reset_password`,
            {
              reset_password_token: this.resetPasswordToken,
              password: this.password,
              password_confirmation: this.passwordConfirmation
            }
          );

          if (response.data.success) {
            this.successMessage = response.data.message ||
              "Tu contraseña ha sido actualizada exitosamente.";

            // Iniciar contador de redirección
            this.startCountdown();
          } else {
            this.errorMessage = response.data.message ||
              "No se pudo actualizar la contraseña.";
          }
        } catch (error) {
          console.error("Error en reset password:", error);
          this.errorMessage =
            error.response?.data?.message ||
            "Error al actualizar la contraseña. El enlace puede haber expirado.";
        } finally {
          this.loading = false;
        }
      }
    },
    goToLogin() {
      // Limpiar el intervalo si existe
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this.$router.push("/login_standard").catch(() => {});
    },
    startCountdown() {
      this.countdown = 5;
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.goToLogin();
        }
      }, 1000);
    }
  }
};
</script>

<style scoped>
.login-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  z-index: 9999;
}

.login-container {
  min-height: 100vh;
  padding: 40px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card {
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

.login-header {
  background: #28385C;
  padding: 40px 40px 36px;
  text-align: center;
  color: white;
}

.logo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.logo-img {
  width: 140px;
  height: auto;
  object-fit: contain;
}

.login-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
}

.login-subtitle {
  font-size: 15px;
  margin: 0;
  opacity: 0.9;
  font-weight: 300;
}

.login-form-container {
  padding: 40px !important;
}

.login-btn {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: none;
  border-radius: 10px;
  height: 56px !important;
  transition: all 0.3s ease;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(40, 56, 92, 0.25) !important;
}

.cancel-btn {
  font-size: 14px;
  font-weight: 500;
  color: #fff;
}

.login-footer {
  padding: 20px 40px;
  text-align: center;
  background: #f8f9fa;
}

.footer-text {
  margin: 0;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Success Container */
.success-container {
  padding: 50px 40px;
  text-align: center;
}

.success-icon-wrapper {
  margin-bottom: 20px;
  animation: successPulse 0.6s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 26px;
  font-weight: 600;
  color: #28385C;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;
}

.success-message {
  font-size: 15px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.redirect-text {
  font-size: 13px;
  color: #999;
  margin: 0;
  font-style: italic;
}

/* Tablet */
@media (max-width: 768px) {
  .login-container {
    padding: 30px 16px;
  }

  .login-card {
    max-width: 460px;
  }

  .login-header {
    padding: 36px 32px 32px;
  }

  .logo-img {
    width: 130px;
  }

  .login-title {
    font-size: 26px;
  }

  .login-subtitle {
    font-size: 14px;
  }

  .login-form-container {
    padding: 32px !important;
  }

  .login-footer {
    padding: 18px 32px;
  }

  .success-container {
    padding: 40px 32px;
  }
}

/* Mobile */
@media (max-width: 480px) {
  .login-container {
    padding: 20px 16px;
  }

  .login-card {
    border-radius: 12px;
  }

  .login-header {
    padding: 28px 24px 24px;
  }

  .logo-img {
    width: 110px;
  }

  .login-title {
    font-size: 22px;
  }

  .login-subtitle {
    font-size: 13px;
  }

  .login-form-container {
    padding: 24px !important;
  }

  .login-btn {
    height: 52px !important;
    font-size: 15px;
  }

  .cancel-btn {
    font-size: 13px;
  }

  .login-footer {
    padding: 16px 24px;
  }

  .footer-text {
    font-size: 12px;
  }

  .success-container {
    padding: 32px 24px;
  }

  .success-title {
    font-size: 22px;
  }

  .success-message {
    font-size: 14px;
  }

  .redirect-text {
    font-size: 12px;
  }
}
</style>
